<template>
  <call-widget v-if="isWebCallingEnabled" @retry="initializePhone" />
</template>

<script>
import { mapGetters } from 'vuex';
import CallWidget from './CallWidget.vue';
import Calling from 'dashboard/api/callling';

/**
 * Owns the softphone's lifecycle and config resolution, keeping CallWidget a
 * pure renderer of store state.
 *
 * Mounted once at the app shell so it survives route changes — a call must not
 * drop because the agent moved between conversations.
 */
export default {
  name: 'CallWidgetContainer',

  components: { CallWidget },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),

    callConfig() {
      const { custom_attributes: customAttributes = {} } =
        this.getAccount(this.currentAccountId) || {};
      return customAttributes.call_config || {};
    },

    providerConfig() {
      return this.callConfig.externalProviderConfig || {};
    },

    isWebCallingEnabled() {
      return Boolean(
        this.callConfig.enabled &&
          this.callConfig.externalProvider === 'exotel' &&
          this.providerConfig.webrtcEnabled &&
          this.providerConfig.webrtcAppId &&
          this.currentUser?.email
      );
    },
  },

  watch: {
    isWebCallingEnabled: {
      immediate: true,
      handler(isEnabled) {
        if (isEnabled) this.initializePhone();
        else this.$store.dispatch('calling/teardown');
      },
    },
  },

  mounted() {
    // Release the cross-tab lease on close so a refresh or a closed tab does
    // not leave other tabs waiting out the lease TTL before registering.
    window.addEventListener('beforeunload', this.teardownPhone);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.teardownPhone);
    this.teardownPhone();
  },

  methods: {
    async initializePhone() {
      this.$store.dispatch('calling/beginSession');
      try {
        const { data } = await Calling.getWebrtcSession({
          accountId: this.currentAccountId,
        });
        await this.$store.dispatch('calling/initialize', {
          accessToken: data.access_token,
          userId: data.user_id,
        });
      } catch (error) {
        // 409 means this agent has no Exotel user: Web Calling simply does
        // not apply to them. The widget stays hidden and their Call button
        // keeps dialling their phone. Anything else is a real failure.
        if (error?.response?.status === 409) {
          this.$store.dispatch('calling/markUnprovisioned');
          return;
        }
        this.$store.dispatch('calling/failSession', {
          message: error?.response?.data?.error,
        });
      }
    },

    teardownPhone() {
      this.$store.dispatch('calling/teardown');
    },
  },
};
</script>
